import {datetimeFormatter, Typography} from '@hconnect/uikit'
import {Box, CircularProgress} from '@material-ui/core'
import moment from 'moment'
import {useTranslation} from 'react-i18next'

import {useOrderCancellationTimeWindow} from '../../Hooks/OrderManagement/useCancellationTimeWindow'
import {Order} from '../../Pages/Order/Order.types'
import {OrderCancellationOptions} from '../declarations/types'

import {Timer} from './TimeLeftToOrder'

interface OrderCancellationCutOffTimeProps {
  order: Order
  onCancelCutOffTimeReached: (isTimeToCancelOver: boolean) => void
  cancellationOptions: OrderCancellationOptions | undefined
  isLoadingCancellationOptions: boolean
  isCancellationButtonDisabled: boolean
}

export const OrderCancellationCutOffTime: React.FC<OrderCancellationCutOffTimeProps> = ({
  order,
  onCancelCutOffTimeReached,
  cancellationOptions,
  isLoadingCancellationOptions,
  isCancellationButtonDisabled
}) => {
  const cancellationTimeWindow = useOrderCancellationTimeWindow(
    order,
    cancellationOptions,
    onCancelCutOffTimeReached
  )

  if (isLoadingCancellationOptions) {
    return <CircularProgress />
  }

  return (
    <>
      {!isCancellationButtonDisabled && (
        <>
          {cancellationTimeWindow.isCancellable && (
            <CancellationUntil cancellableUntil={cancellationTimeWindow.cancellableUntil} />
          )}
          {cancellationTimeWindow.isTimerActive ? (
            <CancellationTimer timer={cancellationTimeWindow.timer} />
          ) : null}
        </>
      )}
    </>
  )
}

const CancellationUntil = ({cancellableUntil}: {cancellableUntil: moment.Moment}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const cancellableDateTime = datetimeFormatter(cancellableUntil, language)
  return (
    <>
      <Typography
        variant="body2"
        customColor="textPrimarySoft"
        style={{fontWeight: 700, fontSize: 14}}
        data-test-id="order-cancellation-cut-off-time"
      >
        {t('orderIntake.cancellationCutOff')}
      </Typography>

      <Typography
        variant="subtitle1"
        style={{fontWeight: 700, fontSize: 12}}
        data-test-id="cancellation-cut-off-time"
      >
        {cancellableDateTime}
      </Typography>
      <Box mt={1} />
    </>
  )
}

const CancellationTimer = ({
  timer
}: {
  timer: {
    hours: number
    minutes: number
    seconds: number
  }
}) => {
  const {t} = useTranslation()
  return (
    <Typography variant="caption" color="error" data-test-id="order-cancellation-timer">
      {t('orderIntake.cancellationTimeLeft')}
      <Timer {...timer} />
    </Typography>
  )
}
